// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { Fragment } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
    IAppError,
    IApplicationState,
    IProject,
    ErrorCode,
    IFormRegistration,
    IFormResetPassword,
    IFormChangePassword,
    IAdHocProject,
    ICompany,
    IAdHocGroupProject,
    IExportGroupProject,
} from "./models/applicationState";
import IAppErrorActions, * as appErrorActions from "./redux/actions/appErrorActions";
import { ErrorHandler } from "./react/components/common/errorHandler/errorHandler";
import { KeyboardManager } from "./react/components/common/keyboardManager/keyboardManager";
import { MainContentRouter } from "./react/components/shell/mainContentRouter";
import { Sidebar } from "./react/components/shell/sidebar";
import { StatusBar } from "./react/components/shell/statusBar";
import { StatusBarMetrics } from "./react/components/shell/statusBarMetrics";
import { TitleBar } from "./react/components/shell/titleBar";

import { getAppInsights } from "./services/telemetryService";
import TelemetryProvider from "./providers/telemetry/telemetryProvider";
import "./App.scss";
import "react-toastify/dist/ReactToastify.css";
import { withCookies } from "react-cookie";
import {
    baseUrl,
    getAuthQuery,
    getCookieEmail    
} from "./react/components/common/jsonSchemaFormHelper";
import IProjectActions, * as projectActions from "./redux/actions/projectActions";
import IApplicationActions, * as applicationActions from "./redux/actions/applicationActions";
import { removeStorageItem } from "./redux/middleware/localStorage";
import IAdHocOcrActions, * as adHocActions from "./redux/actions/adHocOcrActions";
import IAdHocOcrV2Actions, * as aHocV2Actions from "./redux/actions/adHocOcrV2Actions";
import IAdHocOcrV3Actions, * as aHocV3Actions from "./redux/actions/adHocOcrV3Actions";
import { TitleBarV2 } from "./react/components/shell/titleBarV2";

interface IAppProps {
    currentProject?: IProject;
    appError?: IAppError;
    actions?: IAppErrorActions;
    projectActions?: IProjectActions;
    applicationActions?: IApplicationActions;
    widgetMode?: boolean;
    uploadSuccessFlag?: number;
    adHocOcr: IAdHocProject;
    adHocActions: IAdHocOcrActions;
    adHocOcrV2: IAdHocProject,
    aHocV2Actions: IAdHocOcrV2Actions;
    adHocOcrGroup: IAdHocGroupProject;
    adHocOcrV3: IAdHocProject,
    aHocV3Actions: IAdHocOcrV3Actions;
    adHocOcrV3Group: IAdHocGroupProject;
}

function mapStateToProps(state: IApplicationState) {
    return {
        currentProject: state.currentProject,
        appError: state.appError,
        widgetMode: state.appSettings.widgetMode,
        uploadSuccessFlag: state.currentProject ? state.currentProject.uploadSuccessFlag : 0,
        adHocOcr: state.adHocOcr,
        adHocOcrV2: state.adHocOcrV2,
        adHocOcrGroup: state.adHocOcrGroup,
        adHocOcrV3: state.adHocOcrV3,
        adHocOcrV3Group: state.adHocOcrV3Group
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(appErrorActions, dispatch),
        projectActions: bindActionCreators(projectActions, dispatch),
        applicationActions: bindActionCreators(applicationActions, dispatch),
        adHocActions: bindActionCreators(adHocActions, dispatch), 
        aHocV2Actions: bindActionCreators(aHocV2Actions, dispatch),
        aHocV3Actions: bindActionCreators(aHocV3Actions, dispatch)
    };
}

export interface IAppState {
    showLoginDialog: boolean;
    showRegisterDialog: boolean;
    showResetPasswordDialog: boolean;
    currentProject: IProject;
    appError?: IAppError;
    loading: boolean;
    registerData?: IFormRegistration;
    resetPasswordData?: IFormResetPassword;
    linkedIn?: any;
    showSubscriptionDialog: boolean;
    showAdminDashboard: boolean;
    loadingAssets: boolean;
    resetKey: string;
    resetUser: string;
    showChangePasswordDialog: boolean;
    changePasswordData?: IFormChangePassword;
    companies: ICompany[];
    canGenerateModel: boolean;
    runGenerateModel: boolean;
    hasExistingModel: boolean;
    hidePostDialog: boolean;
    groupId: string;
    isPosting: boolean;
}

/**
 * @name - App
 * @description - Root level component for VoTT Application
 */
@connect(mapStateToProps, mapDispatchToProps)
class App extends React.Component<IAppProps, IAppState> {
    appInsights: any = null;
    externalKey = '';
    pathname = '';
    requestId = '';    

    constructor(props, context) {
        super(props, context);
        this.state = {
            currentProject: this.props.currentProject,
            showLoginDialog: false,
            showRegisterDialog: false,
            showResetPasswordDialog: false,
            appError: this.props.appError,
            showSubscriptionDialog: false,
            showAdminDashboard: false,
            loading: false,
            linkedIn: {
                state: window.sessionStorage.getItem("linkedin_state")
                    ? window.sessionStorage.getItem("linkedin_state")
                    : null,
                redirect_uri: baseUrl(),
                profileImage: null,
            } as object,
            loadingAssets: false,
            resetKey: '',
            resetUser: '',
            showChangePasswordDialog: false,
            companies: [],
            canGenerateModel: false,
            runGenerateModel: false,
            hasExistingModel: false,
            hidePostDialog: true, 
            groupId: '', 
            isPosting: false
        };

        this.onReject = this.onReject.bind(this);
        this.onGenerateModelChange = this.onGenerateModelChange.bind(this);
        
    }

    public async componentDidMount() {

        this.props.applicationActions.setWidgetMode();

        removeStorageItem("tagsToDelete");
        let url = new URL(window.location.href);
        this.pathname = url.pathname;
        this.requestId = url.searchParams.get("requestId");
        const groupId = url.searchParams.get("groupId");
        if (groupId) {
            this.setState({ groupId });
        }
        // if (requestId) {
        //     if (this.props.adHocOcr) {
        //         const generateModel = await this.props.adHocActions.hasGenerateModelCredits(requestId);            
        //         let canGenerateModel = false;
        //         if (generateModel && generateModel.length > 0) {
        //             canGenerateModel = generateModel[0];
        //         }
        //         let runGenerateModel = false;
        //         if (generateModel && generateModel.length > 1) {
        //             runGenerateModel = generateModel[1];
        //         }
        //         let hasExistingModel = false;
        //         if (generateModel && generateModel.length > 2) {
        //             hasExistingModel = generateModel[2];
        //         }
        //         this.setState({
        //             canGenerateModel,
        //             runGenerateModel: runGenerateModel || !hasExistingModel
        //         });
        //     }            
        // }        
    }

    async componentDidUpdate(prevProps: Readonly<IAppProps>, prevState: Readonly<IAppState>, snapshot?: any) {
        if (JSON.stringify(prevProps.adHocOcr) != JSON.stringify(this.props.adHocOcr)) {
            const generateModel = await this.props.adHocActions.hasGenerateModelCredits(this.requestId);            
            let canGenerateModel = false;
            if (generateModel && generateModel.length > 0) {
                canGenerateModel = generateModel[0];
            }
            let runGenerateModel = false;
            if (generateModel && generateModel.length > 1) {
                runGenerateModel = generateModel[1];
            }
            let hasExistingModel = false;
            if (generateModel && generateModel.length > 2) {
                hasExistingModel = generateModel[2];
            }
            this.setState({
                canGenerateModel,
                runGenerateModel: runGenerateModel || !hasExistingModel
            });
        }

        if (JSON.stringify(prevProps.adHocOcrV2) != JSON.stringify(this.props.adHocOcrV2)) {
            this.setState({
                runGenerateModel: !this.props.adHocOcrV2.hasModel
            });
        }

        if (JSON.stringify(prevProps.adHocOcrV3) != JSON.stringify(this.props.adHocOcrV3)) {
            this.setState({
                runGenerateModel: !this.props.adHocOcrV3.hasModel
            });
        }
    }

    onGenerateModelChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean): void => {
        this.setState({
            runGenerateModel: checked
        })              
    }

    private onReject = (res) => {
        if (res.response && res.response.status === 401) {
            this.setState({
                showLoginDialog: true
            });
        }
    }

    public componentDidCatch(error: Error) {
        var appError = {
            errorCode: ErrorCode.GenericRenderError,
            title: error.name,
            message: error.message,
        }
        this.props.actions.showError(appError);
        console.log(JSON.stringify(appError));
    }

    private checkIfVerify = () => {
        return getAuthQuery();
    };

    private postToSourceV3 =  async () => {
        if (this.state.isPosting) {
            return;
        }
        this.setState({ isPosting: true }, async () => {
            let exportGroupProject: IExportGroupProject = {
                groupId: this.state.groupId,
                exportDocuments: null
            };
    
            if (this.props.adHocOcrV3Group.projectToPosts && this.props.adHocOcrV3Group.projectToPosts.length > 0) {
                let exportDocs = this.props.adHocOcrV3Group.projectToPosts.map(proj => {
                    let generateModel = false;
                    const projectDetails = this.props.adHocOcrV3Group.adhocGroupProjects.find(p => p.project.id === proj.projectId);
                    if (this.props.adHocOcrGroup && this.props.adHocOcrGroup.generateModelGroup && this.props.adHocOcrGroup.generateModelGroup.some(s => s.val === proj.projectId && s.key === projectDetails.vendor)) {
                        generateModel = true;
                    }
                    return {
                        generateModel, 
                        requestId: proj.projectId
                    }
                });
        
                exportGroupProject = {
                    ...exportGroupProject,
                    exportDocuments: exportDocs
                };
            }
            else {
                let exportDocs = this.props.adHocOcrV3Group.adhocGroupProjects.map(proj => {
                    let generateModel = false;
                    if (this.props.adHocOcrGroup && this.props.adHocOcrGroup.generateModelGroup && this.props.adHocOcrGroup.generateModelGroup.some(s => s.val === proj.project.id)) {
                        generateModel = true;
                    }
                    return {
                        generateModel, 
                        requestId: proj.project.id
                    }
                });
        
                exportGroupProject = {
                    ...exportGroupProject,
                    exportDocuments: exportDocs
                };
            }
            try {
                await this.props.aHocV3Actions.exportGroupProjectLabelToSource(exportGroupProject);
            }
            catch(e) {
                if (e === 'No label file found') {
                    toast.error('Unable to process, at least one label must have a value.')
                }
                // else {
                //     toast.error('An error occured posting to source.');
                // }
            }               
            finally {
                this.setState({ isPosting: false });
            }
        });
        
    }

    private postAdHocToSource = async () => {  
        try {
            if (this.props.adHocOcrV3Group) {
                //this.toggleHidePostDialog();
                this.postToSourceV3();
            }
            else if (this.props.adHocOcrV3) {
                    const asset = this.props.adHocOcrV3.project.assets[Object.keys(this.props.adHocOcrV3.project.assets)[0]];
                    await this.props.aHocV3Actions.exportProjectLabelToSource(this.props.adHocOcrV3.project.id, this.state.runGenerateModel);                                
            }            
            else {
                if (this.props.adHocOcrV2 && this.props.adHocOcrV2.isV2) {                
                    if (this.props.adHocOcrGroup) {                    
                        let exportDocs = this.props.adHocOcrGroup.adhocGroupProjects.map(proj => {
                            let generateModel = false;
                            if (this.props.adHocOcrGroup && this.props.adHocOcrGroup.generateModelGroup && this.props.adHocOcrGroup.generateModelGroup.some(s => s.val === proj.project.id)) {
                                generateModel = true;
                            }
                            return {
                                generateModel, 
                                requestId: proj.project.id
                            }
                        });
    
                        let exportGroupProject: IExportGroupProject = {
                            groupId: this.state.groupId,
                            exportDocuments: exportDocs
                        };
    
                        await this.props.aHocV2Actions.exportGroupProjectLabelToSource(exportGroupProject);
                    }
                    else {
                        await this.props.aHocV2Actions.exportProjectLabelToSource(this.props.adHocOcrV2.project.id, this.state.runGenerateModel);
                    }                
                }
                else {
                    const asset = this.props.adHocOcr.project.assets[Object.keys(this.props.adHocOcr.project.assets)[0]];
                    await this.props.adHocActions.exportProjectLabelToSource(this.props.adHocOcr.project.id, this.props.adHocOcr.project.name, asset.name, this.state.runGenerateModel);                
                }
            }            
            // var result = await this.props.adHocActions.exportProjectLabelToSource(this.props.adHocOcr.project.id, this.props.adHocOcr.project.name, asset.name, this.state.runGenerateModel);
            // if (result) {
            // }
        }
        catch(e) {
            if (e === 'No label file found') {
                toast.error('Unable to process, at least one label must have a value.')
            }
            else {
                toast.error('An error occured posting to source.');
            }
        }
    }

    toggleHidePostDialog = () => {
        this.setState({
            hidePostDialog: !this.state.hidePostDialog,
        })
    }

    public render() {
        const platform =
            global && global.process ? global.process.platform : "web";

        const { widgetMode } = this.props;

        return (
            <Fragment>
                <ErrorHandler
                    error={this.props.appError}
                    onError={this.props.actions.showError}
                    onClearError={this.props.actions.clearError}
                />

                {/* Don't render app contents during a render error */}
                {(!this.props.appError ||
                    this.props.appError.errorCode !==
                        ErrorCode.GenericRenderError) && (
                    <KeyboardManager>
                        <BrowserRouter>
                            <TelemetryProvider
                                after={() => {
                                    this.appInsights = getAppInsights();
                                }}
                            >
                                <div
                                    className={`app-shell platform-${platform}`}
                                >
                                    {!!this.props.adHocOcrV2 || !!this.props.adHocOcrV3 ?
                                        (
                                            <TitleBarV2
                                                icon="CloudUpload"
                                                project={this.props.currentProject}
                                                widgetMode={widgetMode}
                                                postAdHocToSource={this.postAdHocToSource}
                                                canGenerateModel={this.state.canGenerateModel}
                                                onGenerateModelChange={this.onGenerateModelChange}
                                                runGenerateModel={this.state.runGenerateModel}
                                                hasExistingModel={this.state.hasExistingModel}
                                                adHocOcrV2={this.props.adHocOcrV2}
                                                adHocOcrGroup={this.props.adHocOcrGroup}
                                                adHocOcrV3={this.props.adHocOcrV3}
                                                projectToPosts={this.props.adHocOcrV3Group ? this.props.adHocOcrV3Group.projectToPosts : null}
                                                hidePostDialog={this.state.hidePostDialog}
                                                toggleHidePostDialog={this.toggleHidePostDialog}
                                                postToSourceV3={this.postToSourceV3}
                                                groupId={this.state.groupId}
                                                isPosting={this.state.isPosting}
                                            />                                        
                                        ) : (
                                            <TitleBar
                                                icon="CloudUpload"
                                                project={this.props.currentProject}
                                                widgetMode={widgetMode}
                                                adHocOcr={this.props.adHocOcr}
                                                postAdHocToSource={this.postAdHocToSource}
                                                canGenerateModel={this.state.canGenerateModel}
                                                onGenerateModelChange={this.onGenerateModelChange}
                                                runGenerateModel={this.state.runGenerateModel}
                                                hasExistingModel={this.state.hasExistingModel}
                                                adHocOcrV2={this.props.adHocOcrV2}
                                            />
                                        )
                                    }

                                    <div className="app-main">
                                        <Sidebar
                                            project={this.props.currentProject}
                                            widgetMode={widgetMode}
                                        />
                                        <MainContentRouter />
                                    </div>
                                    <StatusBar
                                        project={this.props.currentProject}
                                    >
                                        <StatusBarMetrics
                                            project={this.props.currentProject}
                                            email={getCookieEmail()}
                                        />
                                        <ul>
                                            <li title={`App Version (${process.env.REACT_APP_HATCHET_VERSION})`}>
                                                <span className="app-version">@{process.env.REACT_APP_HATCHET_VERSION}</span>
                                            </li>
                                        </ul>
                                    </StatusBar>
                                    <ToastContainer
                                        className="frtt-toast-container"
                                        role="alert" 
                                        hideProgressBar={false}
                                        position="top-right" 
                                        autoClose={5000}
                                    />
                                </div>
                            </TelemetryProvider>
                        </BrowserRouter>
                    </KeyboardManager>
                )}                
            </Fragment>
        );
    }
}

export default withCookies(App);
